import React, { useState, cloneElement, isValidElement } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import './FAB.scss';

/**
 * Floating Action Button (FAB) component
 * @param {Object} props Component props
 * @param {Function} props.onClick Click handler function for the main button (when no options are provided)
 * @param {Object} props.icon FontAwesome icon to display (default: faPlus)
 * @param {string} props.className Additional CSS class names
 * @param {string} props.color Button color (primary, success, danger, warning)
 * @param {string} props.tooltip Tooltip text for the button
 * @param {string} props.position Position of the FAB ('bottom-right', 'bottom-left', 'top-right', 'top-left')
 * @param {Array} props.options Array of option objects with icon, color, tooltip, onClick/component, and isVisible properties
 * @param {string} props.direction Direction in which options open ('up', 'down', 'left', 'right')
 * @param {Object} props.offset Optional offset values for x and y positions (in pixels)
 * @param {React.Component} props.component Optional React component (like Link) to use instead of button
 * @param {Object} props.componentProps Props to pass to the component
 */
const FAB = ({
  onClick,
  icon = faPlus,
  className = '',
  color = 'primary',
  tooltip = '',
  position = 'bottom-right',
  options = [],
  direction = 'up',
  offset = { x: 0, y: 0 },
  component,
  componentProps = {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const baseClass = 'fab-button';
  const colorClass = `fab-${color}`;
  const positionClass = `fab-${position}`;
  const directionClass = `fab-direction-${direction}`;
  
  // Create style with offset
  const offsetStyle = {};
  if (offset.x || offset.y) {
    if (position.includes('right')) {
      offsetStyle.right = `calc(24px + ${offset.x}px)`;
    } else if (position.includes('left')) {
      offsetStyle.left = `calc(24px + ${offset.x}px)`;
    }
    
    if (position.includes('bottom')) {
      offsetStyle.bottom = `calc(24px + ${offset.y}px)`;
    } else if (position.includes('top')) {
      offsetStyle.top = `calc(24px + ${offset.y}px)`;
    }
  }
  
  const toggleOptions = () => {
    setIsOpen(!isOpen);
  };
  
  const handleMainClick = () => {
    if (options && options.length > 0) {
      toggleOptions();
    } else if (onClick) {
      onClick();
    }
  };
  
  return (
    <div className={`fab-container ${positionClass} ${isOpen ? 'fab-opened' : ''} ${directionClass}`} style={offsetStyle}>
      {options && options.length > 0 && isOpen && (
        <div className={`fab-options fab-${direction}`}>
          {options.filter(option => option.isVisible !== false).map((option, index) => {
            // Handle both component (Link) and button options
            if (option.component && isValidElement(option.component)) {
              return cloneElement(
                option.component,
                {
                  key: index,
                  className: `fab-option-button fab-${option.color || 'primary'} ${option.component.props.className || ''}`,
                  title: option.tooltip || '',
                  'aria-label': option.tooltip || `Option ${index + 1}`,
                  onClick: (e) => {
                    e.stopPropagation();
                    if (option.component.props.onClick) {
                      option.component.props.onClick(e);
                    }
                    setIsOpen(false);
                  },
                  ...option.componentProps
                },
                <>
                  <FontAwesomeIcon icon={option.icon || faPlus} className="fab-option-icon" />
                  <span className="fab-option-text">{option.tooltip || 'Action'}</span>
                </>
              );
            }
            
            return (
              <button
                key={index}
                className={`fab-option-button fab-${option.color || 'primary'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (option.onClick) {
                    option.onClick();
                  }
                  setIsOpen(false);
                }}
                title={option.tooltip || ''}
                aria-label={option.tooltip || `Option ${index + 1}`}
              >
                <FontAwesomeIcon icon={option.icon || faPlus} className="fab-option-icon" />
                <span className="fab-option-text">{option.tooltip || 'Action'}</span>
              </button>
            );
          })}
        </div>
      )}
      {component && isValidElement(component) ? (
        cloneElement(
          component,
          {
            className: `${baseClass} ${colorClass} ${className} ${isOpen ? 'fab-active' : ''} ${component.props.className || ''}`,
            title: tooltip,
            'aria-label': tooltip || 'Action button',
            onClick: handleMainClick,
            ...componentProps
          },
          <FontAwesomeIcon icon={icon} />
        )
      ) : (
        <button
          className={`${baseClass} ${colorClass} ${className} ${isOpen ? 'fab-active' : ''}`}
          onClick={handleMainClick}
          title={tooltip}
          aria-label={tooltip || 'Action button'}
        >
          <FontAwesomeIcon icon={icon} />
        </button>
      )}
    </div>
  );
};

FAB.propTypes = {
  onClick: PropTypes.func,
  icon: PropTypes.object,
  className: PropTypes.string,
  color: PropTypes.oneOf(['primary', 'success', 'danger', 'warning', 'info']),
  tooltip: PropTypes.string,
  position: PropTypes.oneOf(['bottom-right', 'bottom-left', 'top-right', 'top-left']),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.object,
      color: PropTypes.oneOf(['primary', 'success', 'danger', 'warning', 'info']),
      tooltip: PropTypes.string,
      onClick: PropTypes.func,
      component: PropTypes.element,
      componentProps: PropTypes.object,
      isVisible: PropTypes.bool
    })
  ),
  direction: PropTypes.oneOf(['up', 'down', 'left', 'right']),
  offset: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  }),
  component: PropTypes.element,
  componentProps: PropTypes.object
};

export default FAB;
